import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { HotelSubBookingData } from '@shared/models/hotel-search-payload.model';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import { ITravelHotelSubBooking } from '@shared/shared/interfaces/travel-hotel.interfaces';
import { getHotelSubBookingData } from '@shared/store/travel-search/selectors/hotel-search.selectors';
import * as UrlLib from '@shared/utils/url.lib';
import { SELECTED_ROOM_LOCAL_STORAGE_KEY, TRAVEL_HOTEL_SUB_BOOKING_LOCAL_STORAGE_KEY } from 'app.config';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import * as fromHotelDetailsActions from '../../travel-search/actions/hotel-details.actions';
import * as travelBookingProgressBarActions from '../actions/travel-booking-progress-bar.actions';
import * as fromActions from '../actions/travel-hotel-sub-booking.actions';
import { TravelBookingState } from '../reducers';
import * as fromReducers from '../reducers/travel-hotel-sub-booking.reducers';
import { GetTravelBookingBarState } from '../selectors';
import { getTravelHotelSubBookingFeatureState } from '../selectors/travel-sub-booking.selectors';

@Injectable()
export class TravelHotelSubBookingEffects {
  constructor(
    private actions$: Actions,
    private ngrxStore: Store<{ 'travel-booking': TravelBookingState }>,
    private http: HttpClient,
    private router: Router
  ) {}

  public clearHotelSubBooking$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.CLEAR_HOTEL_SUB_BOOKING),
        tap(() => {
          localStorage.removeItem(TRAVEL_HOTEL_SUB_BOOKING_LOCAL_STORAGE_KEY);
          localStorage.removeItem(SELECTED_ROOM_LOCAL_STORAGE_KEY);
        })
      ),
    { dispatch: false }
  );

  public createHotelSubBooking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.CREATE_HOTEL_SUB_BOOKING),
      withLatestFrom(this.ngrxStore.select(getHotelSubBookingData)),
      switchMap(([_, subBooking]: [fromActions.CreateHotelSubBooking, HotelSubBookingData]) => {
        const payload = {
          ...subBooking,
        };
        return this.http.post(ApiEndpoint.TRAVEL_HOTEL_SUB_BOOKING, payload).pipe(
          map((res: ITravelHotelSubBooking) => {
            this.createSubBookingSuccess(res);
            this.routeToNextPage();
            return new fromActions.CreateHotelSubBookingSuccess(res);
          }),
          catchError((err) => {
            return of(new fromActions.CreateHotelSubBookingFailure(err));
          })
        );
      })
    )
  );

  public patchHotelSubBooking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.PATCH_HOTEL_SUB_BOOKING_TRAVELLERS),
      withLatestFrom(this.ngrxStore.select(getTravelHotelSubBookingFeatureState)),
      switchMap(([action, subBooking]: [fromActions.PatchHotelSubBookingTravellers, ITravelHotelSubBooking]) => {
        const url = UrlLib.replaceTemplateKeys(ApiEndpoint.TRAVEL_HOTEL_SUB_BOOKING_PATCH_TRAVELLERS, {
          id: subBooking.id,
        });
        const payload = this.createPatchPayload(action.payload);
        return this.http.patch(url, payload).pipe(
          map((res) => {
            return new fromActions.PatchHotelSubBookingTravellersSuccess(res);
          }),
          catchError((err) => of(new fromActions.PatchHotelSubBookingTravellersFailure(err)))
        );
      })
    )
  );

  private routeToNextPage() {
    this.ngrxStore
      .select(GetTravelBookingBarState)
      .pipe(take(1))
      .subscribe((value) => {
        if (value.is_car_hire_search_complete) {
          this.router.navigate(['/travel/car-hire-results/']);
        } else {
          this.router.navigate([`/booking/travel/`]);
        }
      });
  }

  private createPatchPayload(data: any): { travellers: number[] } {
    const travellers = Object.keys(data).reduce(
      (res, key) => (/^leadGuest\d$/.test(key) ? [...res, data[key]] : res),
      []
    );
    return { travellers };
  }

  public viewHotelSubBooking$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.VIEW_HOTEL_SUB_BOOKING),
      withLatestFrom(this.ngrxStore),
      switchMap(([, state$]) => {
        const state = state$['travel-booking'].travelHotelSubBooking;
        return this.viewSubBooking(state);
      })
    )
  );

  /**
   * Either gets the sub booking info form the state if it's present or makes an API call to get it
   *
   * @param state
   */
  public viewSubBooking(state: ITravelHotelSubBooking): Observable<any> {
    if (state && state !== fromReducers.initialState) {
      return of(new fromActions.ViewHotelSubBookingSuccess(state));
    } else {
      const json = localStorage.getItem(TRAVEL_HOTEL_SUB_BOOKING_LOCAL_STORAGE_KEY);
      if (json !== null) {
        const hotel_sub_booking_id = JSON.parse(json).hotel_sub_booking_id;
        const url = UrlLib.replaceTemplateKeys(ApiEndpoint.TRAVEL_HOTEL_SUB_BOOKING, { id: hotel_sub_booking_id });

        return this.http.get(url).pipe(
          map((res: ITravelHotelSubBooking) => {
            this.ngrxStore.dispatch(new fromHotelDetailsActions.GetHotelDetail(+res.hotel_ivector_connect_id));
            return new fromActions.ViewHotelSubBookingSuccess(res);
          }),
          catchError((err) => {
            return of(new fromActions.ViewHotelSubBookingFailure(err));
          })
        );
      } else {
        return of(new fromActions.ViewHotelSubBookingSuccess(fromReducers.initialState));
      }
    }
  }

  /**
   * Save the sub booking response to local storage and update the local booking
   * @param data
   */
  public createSubBookingSuccess(data: ITravelHotelSubBooking) {
    const hotel_sub_booking_id = data.id ? data.id : '';
    localStorage.setItem(TRAVEL_HOTEL_SUB_BOOKING_LOCAL_STORAGE_KEY, JSON.stringify({ hotel_sub_booking_id }));
    this.ngrxStore.dispatch(
      new travelBookingProgressBarActions.UpdateTravelBookingBar({ is_hotel_details_complete: true })
    );
  }
}
